const SHOPIFY_VARIANT_FIELDS = {
    COLOR: 'color',
    SIZE: 'size'
}

const normalizePromoText = (value) => {
    if (typeof value !== 'string') return ''
    const trimmed = value.trim()
    if (!trimmed || trimmed.toLowerCase() === 'null') return ''

    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
            const parsed = JSON.parse(trimmed)
            if (Array.isArray(parsed)) {
                return parsed.find((entry) => typeof entry === 'string' && entry.trim().length > 0)?.trim() || ''
            }

            if (typeof parsed === 'string') {
                return parsed.trim()
            }
        } catch (error) {
            // Fall back to the trimmed string if JSON parsing fails
            return trimmed
        }
    }

    return trimmed
}

export const handleize = (value) => {
    if (!value) return ''

    return value
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

export const getCustomFieldValue = (sku, fieldKey) => {
    return sku?.customFields?.find((field) => field.key === fieldKey)?.value
}

export const normalizeShopifyVariant = (node, fallbackPrice, productPromoText) => {
    if (!node) return null

    const sizeOption = node.selectedOptions?.find((option) => option.name?.toLowerCase() === SHOPIFY_VARIANT_FIELDS.SIZE)?.value
    const colorOption = node.selectedOptions?.find((option) => option.name?.toLowerCase() === SHOPIFY_VARIANT_FIELDS.COLOR)?.value

    const legacyId = node.id?.split('/')?.pop()

    const customFields = []
    if (sizeOption) customFields.push({ key: SHOPIFY_VARIANT_FIELDS.SIZE, value: sizeOption })
    if (colorOption) customFields.push({ key: SHOPIFY_VARIANT_FIELDS.COLOR, value: colorOption })

    const parsedPrice = Number(node.price?.amount)
    const parsedCompareAtPrice = Number(node.compareAtPrice?.amount)

    const variantPromoText = normalizePromoText(node?.variantPromo?.value)
    const fallbackPromoText = normalizePromoText(productPromoText)
    const promoText = variantPromoText || fallbackPromoText

    return {
        id: legacyId || node.id,
        name: node.title,
        price: Number.isFinite(parsedPrice) ? parsedPrice : fallbackPrice,
        listPrice: Number.isFinite(parsedCompareAtPrice) ? parsedCompareAtPrice : null,
        availability: node.availableForSale && (typeof node.quantityAvailable !== 'number' || node.quantityAvailable > 0) ? 'InStock' : 'OutOfStock',
        inventoryLevel: typeof node.quantityAvailable === 'number' ? node.quantityAvailable : 0,
        customFields,
        promoText: promoText
    }
}

export const mergeProductSkus = (productSkus, externalSkus) => {
    const map = new Map()
    const skus = Array.isArray(productSkus) ? productSkus : []
    const externals = Array.isArray(externalSkus) ? externalSkus : []

    const addSku = (sku, preferExisting = false) => {
        if (!sku) return

        const size = getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.SIZE)
        const color = getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.COLOR)

        if (!size && !color) return

        const key = `${color || ''}|${size || ''}`
        const existing = map.get(key)

        if (existing) {
            const mergedSku = {
                ...(!preferExisting ? sku : existing),
                ...(preferExisting ? sku : existing),
                customFields: existing.customFields?.length ? existing.customFields : (sku.customFields || [])
            }

            if (preferExisting) {
                if ((mergedSku.listPrice == null || Number.isNaN(mergedSku.listPrice)) && existing.listPrice != null) {
                    mergedSku.listPrice = existing.listPrice
                }

                if ((mergedSku.price == null || Number.isNaN(mergedSku.price)) && existing.price != null) {
                    mergedSku.price = existing.price
                }
            }

            const incomingPromo = normalizePromoText(sku?.promoText)
            const existingPromo = normalizePromoText(existing?.promoText)
            mergedSku.promoText = incomingPromo || existingPromo || ''

            // Always prefer Shopify GraphQL availability and inventory data over Nosto data
            // 'existing' contains Shopify data (added first), 'sku' contains Nosto data
            if (existing.availability != null) {
                mergedSku.availability = existing.availability
            }
            if (typeof existing.inventoryLevel === 'number') {
                mergedSku.inventoryLevel = existing.inventoryLevel
            }

            map.set(key, mergedSku)
        } else {
            map.set(key, {
                ...sku,
                promoText: normalizePromoText(sku?.promoText)
            })
        }
    }

    externals.forEach((sku) => addSku(sku, false))
    skus.forEach((sku) => addSku(sku, true))

    return Array.from(map.values())
}

export const buildOptionsFromSkus = (skus) => {
    if (!Array.isArray(skus) || skus.length === 0) {
        return { optionNames: [], optionsWithValues: {} }
    }

    const colors = [...new Set(skus.map((sku) => getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.COLOR)).filter(Boolean))]
    const sizes = [...new Set(skus.map((sku) => getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.SIZE)).filter(Boolean))]

    const optionNames = []
    const optionsWithValues = {}

    if (colors.length > 0) {
        optionNames.push('Color')
        optionsWithValues.Color = colors
    }

    if (sizes.length > 0) {
        optionNames.push('Size')
        optionsWithValues.Size = sizes
    }

    return { optionNames, optionsWithValues }
}

export const createVariantsFromSkus = (skus, optionNames) => {
    if (!Array.isArray(skus) || skus.length === 0) return []

    return skus.map((sku) => {
        const colorField = getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.COLOR)
        const sizeField = getCustomFieldValue(sku, SHOPIFY_VARIANT_FIELDS.SIZE)

        const options = {}
        optionNames.forEach((optName, index) => {
            if (optName.toLowerCase() === 'color') {
                options[`option${index + 1}`] = colorField
            } else if (optName.toLowerCase() === 'size') {
                options[`option${index + 1}`] = sizeField
            }
        })

        return {
            id: sku.id,
            available: sku.availability === 'InStock' && sku.inventoryLevel > 0,
            price: sku.price,
            listPrice: sku.listPrice,
            inventoryLevel: sku.inventoryLevel,
            promoText: normalizePromoText(sku?.promoText),
            ...options
        }
    })
}

export const getOptionIndexes = (optionNames) => {
    const colorKey = optionNames.find((option) => option.toLowerCase() === 'color')
    const sizeKey = optionNames.find((option) => option.toLowerCase() === 'size')

    return {
        colorKey,
        sizeKey,
        colorOptionIndex: optionNames.indexOf(colorKey),
        sizeOptionIndex: optionNames.indexOf(sizeKey)
    }
}
